import type {
  AdditionalPaymentMode,
  CampaignAdditionalPaymentResource,
  ParticipantStatus,
  SharedBudgetPayload,
  SharedBudgetServer,
  SharedBudgetServerPayload,
} from '@metapic-apps/shared'
import type { UploadRawFile } from 'element-plus'

import type { AdvertiserListItemType } from './advertiser.types'
import type { CampaignStatusEnum } from './campaigns.types'

import type {
  MoneyServerType,
  ProcessableImageType,
  ProcessableServerImageType,
  TargetItemType,
} from '@/lib/types/common.types'
import type {
  TrafficSourceCostPayloadType,
  TrafficSourceCostServerType,
  TrafficSourceCostsType,
} from '@/lib/types/traffic-source-costs.types'

export type CampaignServerType = {
  id: number
  campaign_title?: string
  campaign_text?: string
  has_product_seeding: boolean
  additional_payment?: CampaignAdditionalPaymentResource
  image_url?: string
  image?: ProcessableServerImageType
  store_id: number
  store_name: string
  store_group_id: number
  store_logo?: ProcessableServerImageType
  currency: string
  token: string
  traffic_sources_costs?: TrafficSourceCostServerType[]
  type: CampaignTypeEnum
  status: CampaignStatusEnum
  created_at: string
  updated_at: string
  priority: number
  show_for_advertiser: boolean
  name: string
  shared_budget?: SharedBudgetServer
  budget_limit?: number
  consumed_budget?: {
    amount: number
    currency: string
  }
  constraints: {
    per_user_limit: boolean
    current_clicks?: number
    max_clicks?: number
    valid_from?: string
    valid_until?: string
    current_money?: number
  }
  todo: string[]
  hashtags?: string[]
}

export enum PreregisterItemEnum {
  SocialMediaUser = 'socialMediaUser',
  Email = 'email',
}

export enum CampaignTypeEnum {
  Standard = 'standard',
  UserAccepts = 'user_accept',
  StoreAccepts = 'store_accept',
  Suggestion = 'suggestion',
}

export type PreregisterItemType = { type: PreregisterItemEnum; identifier: string; label?: string }
export type TodoListType = { key: number; value: string }[]
export type CampaignFormType = {
  campaignType: CampaignTypeEnum
  internalName: string
  title: string
  description: string
  advertiserGroup: number
  advertiserList: AdvertiserListItemType[]
  hasTargetAllCreators: boolean
  hasPreregisterCreators: boolean
  preregisterItems: PreregisterItemType[]
  targets: TargetItemType[]
  hasUnmatchedTargets?: boolean
  hasProductSeeding: boolean
  isShownForAdvertiser?: boolean
  hasTodoItems?: boolean
  hasBadge: boolean
  todoList: TodoListType
  hashtags: string[]
  tempCampaignImage?: Blob | UploadRawFile | undefined
  invoiceAdvertiserDirectly: boolean
  hasCommission: boolean
  hasAdditionalPayment: boolean
  hasInvoiceAdvertiserDirectly: boolean
  currency: string
  creatorCost?: number
  revenueInvoice?: number
  trafficSourceCosts?: TrafficSourceCostsType[]
  validFrom?: string | Date
  validUntil?: string | Date
  hasStartNow: boolean
  hasOpenEnd: boolean
  currentClicks?: number
  maxClicks?: number
  currentBudgetSpent?: number
  budgetLimit?: number
  perUserLimit: boolean
  hasBudget?: boolean
  image?: ProcessableImageType
  sharedBudget?: SharedBudgetPayload
  extra?: {
    id: number
    token: string
    status: CampaignStatusEnum
  }
  priority?: Record<number, number | null | undefined>
}

export type CampaignType = CampaignFormType & { advertiserId: number }

export type TargetsServerType = {
  user_ids: number[]
  user_tag_ids: number[]
  store_group_ids: number[]
  client_ids: number[]
  revenue_tier_ids: number[]
  emails: string[]
  social_media_identifiers: string[]
  users: { id: number; username: string | null }[]
  user_tags: { id: number; name: string }[]
  revenue_tiers: { id: number; name: string; client_name: string }[]
  clients: { id: number; name: string }[]
  segment_ids?: string[]
  segments?: { id: string; name: string }[]
}

export type TargetsPayloadType = {
  user_ids?: number[]
  user_tag_ids?: number[]
  store_group_ids?: number[]
  emails?: string[]
  social_media_identifiers?: string[]
  client_ids?: number[]
  revenue_tier_ids?: number[]
  segment_ids?: string[]
}

type BaseCreationPayloadType = {
  store_id: number
  store_group_id?: number
  type: CampaignTypeEnum
  campaign_title?: string
  has_product_seeding: boolean
  per_user_limit: boolean
  targets?: TargetsPayloadType
  campaign_text?: string
  todo?: string[]
  valid_from?: string | null
  valid_until?: string | null
  max_clicks?: number | null
  budget_limit?: number | null
  budget_limit_v2?: MoneyServerType | null
  shared_budget_id?: number | null
  shared_budget?: SharedBudgetServerPayload
  traffic_sources_costs?: TrafficSourceCostPayloadType[]
  name?: string
  show_for_advertiser?: boolean
  hashtags?: string[]
}

type AdditionalPaymentPayload = {
  mode: AdditionalPaymentMode
  cost_creator: MoneyServerType
  revenue_invoice?: MoneyServerType
}

export type CampaignCreationPayloadType = BaseCreationPayloadType &
  (
    | {
        additional_payment: AdditionalPaymentPayload
      }
    | { has_onetime_payment: false }
  )

export type CampaignFormSerializedDataType = {
  campaigns: CampaignCreationPayloadType[]
  image?: FormData
}

export type CampaignDetailsCreatorsListPayloadType = {
  id: string
  user_id: number
  display_name: string | null
  status: ParticipantStatus
  links_created: number
  content_created: number
  clicks: number
  estimated_gp: number
  estimated_earning_to_cost_ratio: number
  estimated_media_value: { amount: string; currency: string }
  creator_avatar?: ProcessableServerImageType
  overrides?: {
    // 0 - campaign's click limit for each user manually removed
    click_limit?: number
    traffic_sources_costs?: TrafficSourceCostServerType[]
  }
}[]

export type CampaignDetailsCreator = {
  displayName: string | null
  id: string
  userId: number
  linksCount: number
  contentCount: number
  clicksCount: number
  gpRatio: number
  estimatedGp: number
  estimatedMediaValue: number
  avatar?: ProcessableImageType
  invitePending?: boolean
  overrides: {
    // if click limit was manually deleted it will be `0`, if it was never set it will be `null`
    clickLimit: number | null
    trafficSourcesCosts: TrafficSourceCostsType[]
  }
}

export enum CampaignCreatorsActiveStatusFilter {
  Active = 'active',
  Inactive = 'inactive',
}

export type CampaignStatsPayloadType = {
  summary: {
    clicks: number
    links_created: number
    orders: number
    order_value: number
    estimated_cost: number
    estimated_earning: number
    estimated_gp: number
    estimated_earning_to_cost_ratio: number
  }
  daily_stats: {
    clicks: number
    estimated_cost: number
    estimated_earning: number
    estimated_gp: number
    estimated_earning_to_cost_ratio: number
    links_created: number
    order_value: number
    orders: number
    period: number
    period_start_date: string
    period_end_date: string
    sources: {
      clicks: number
      estimated_cost: number
      estimated_earning: number
      estimated_gp: number
      estimated_earning_to_cost_ratio: number
      order_value: number
      orders: number
      source: 0 | 1 | 2
    }[]
  }[]
}
